import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Flame, TrendingUp } from "lucide-react";

const TREND_STYLE = {
  hot: { bg: "bg-red-50", border: "border-red-200", badge: "bg-red-500 text-white", label: "Hot" },
  warm: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-500 text-white", label: "Warm" },
  cooling: { bg: "bg-slate-50", border: "border-slate-200", badge: "bg-slate-400 text-white", label: "Cooling" },
};

export default function DealVelocityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/admin/deal-velocity`); setData(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">No data</p>;

  return (
    <div className="space-y-4" data-testid="deal-velocity-panel">
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-red-600">{data.summary.total_claims_24h}</p>
          <p className="text-[10px] text-red-400">Claims 24h</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-amber-600">{data.summary.total_claims_7d}</p>
          <p className="text-[10px] text-amber-400">Claims 7d</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-red-600">{data.summary.hot_deals}</p>
          <p className="text-[10px] text-red-400">Hot Deals</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-amber-600">{data.summary.warm_deals}</p>
          <p className="text-[10px] text-amber-400">Warm Deals</p>
        </div>
      </div>

      {data.deals.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No claims yet — data populates as users claim deals</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="velocity-list">
          {data.deals.map((d) => {
            const style = TREND_STYLE[d.trend] || TREND_STYLE.cooling;
            return (
              <div key={d.business_id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${style.bg} ${style.border}`}>
                {d.logo_url ? (
                  <BrandLogo name={d.name} logoUrl={d.logo_url} size="sm" />
                ) : (
                  <BrandLogo name={d.name} size="sm" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{d.name}</p>
                  <p className="text-[10px] text-slate-400">{d.claims_24h} / {d.claims_7d} / {d.claims_30d} (24h/7d/30d)</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${style.badge}`}>{style.label}</span>
                <span className="text-sm font-bold text-slate-600">{d.velocity_score}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
